import { apiClient } from '@/lib/api';

interface NotificationPreferences {
  sound_enabled: boolean;
  desktop_enabled: boolean;
  email_enabled: boolean;
  sound_volume: number;
}

interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class NotificationService {
  private static instance: NotificationService;
  private preferences: NotificationPreferences | null = null;
  private permission: NotificationPermission = 'default';
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  private initialized: boolean = false;

  private constructor() {
    // Don't auto-initialize to avoid auth errors on initial load
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private async initializeNotifications() {
    // Prevent multiple initializations
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return;
    }

    // Get current permission status
    this.permission = Notification.permission;
  }

  async loadPreferences(forceRefresh: boolean = false): Promise<NotificationPreferences> {
    // Don't use local cache - let React Query handle caching
    // This prevents stale data issues when the query refetches
    
    // Initialize notifications on first preferences load
    if (!this.initialized) {
      await this.initializeNotifications();
    }
    
    try {
      const response = await apiClient<NotificationPreferences>('/api/notifications/preferences');
      this.preferences = response;
      return this.preferences;
    } catch (error) {
      // Silently handle auth errors on initial load
      if ((error as any)?.message?.includes('Authentication')) {
        // Return default preferences for unauthenticated users
        this.preferences = {
          sound_enabled: true,
          desktop_enabled: true,
          email_enabled: false,
          sound_volume: 50
        };
        return this.preferences;
      }
      
      console.error('Failed to load notification preferences:', error);
      // Return default preferences
      this.preferences = {
        sound_enabled: true,
        desktop_enabled: true,
        email_enabled: false,
        sound_volume: 50
      };
      return this.preferences;
    }
  }

  async updatePreferences(updates: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    try {
      const response = await apiClient<NotificationPreferences>('/api/notifications/preferences', { 
        method: 'PATCH', 
        data: updates 
      });
      
      this.preferences = response;
      return response;
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error;
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      // Update permission state
      this.permission = 'granted';
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    }

    this.permission = Notification.permission;
    return Notification.permission;
  }

  private async subscribeToPush(): Promise<void> {
    if (!this.serviceWorkerRegistration) {
      console.warn('Service worker not registered');
      return;
    }

    try {
      // Check if already subscribed
      const existingSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        await this.sendSubscriptionToServer(existingSubscription);
        return;
      }

      // Subscribe to push notifications
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        console.warn('VAPID public key not configured');
        return;
      }

      const subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      await this.sendSubscriptionToServer(subscription);
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
    }
  }

  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    try {
      const subscriptionData = subscription.toJSON();
      await apiClient('/api/notifications/push/subscribe', {
        method: 'POST',
        data: {
          endpoint: subscriptionData.endpoint,
          keys: {
            p256dh: subscriptionData.keys?.p256dh || '',
            auth: subscriptionData.keys?.auth || ''
          }
        }
      });
    } catch (error) {
      console.error('Failed to send subscription to server:', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async showNotification(title: string, options?: NotificationOptions): Promise<void> {
    // Check if desktop notifications are enabled in preferences
    if (!this.preferences?.desktop_enabled) {
      return;
    }

    // Check browser permission
    if (this.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        return;
      }
    }

    // Show basic notification
    new Notification(title, {
      icon: '/icon-192.png',
      ...options
    });
  }

  async markMessageAsRead(messageId: string): Promise<void> {
    try {
      await apiClient(`/api/notifications/messages/${messageId}/read`, {
        method: 'PATCH'
      });
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      throw error;
    }
  }

  async markConversationAsRead(conversationId: string): Promise<void> {
    try {
      await apiClient(`/api/notifications/conversations/${conversationId}/read-all`, {
        method: 'PATCH'
      });
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
      throw error;
    }
  }


  isNotificationEnabled(): boolean {
    return this.permission === 'granted' && this.preferences?.desktop_enabled === true;
  }

  isSoundEnabled(): boolean {
    return this.preferences?.sound_enabled === true;
  }

  getSoundVolume(): number {
    return this.preferences?.sound_volume || 50;
  }
}

export default NotificationService.getInstance();